/**
 * Per-user warmup flag for SessionProvider.
 *
 * Persisted across page reloads so returning users on an already-provisioned
 * workspace don't see the "Preparing your workspace…" splash on every
 * refresh — see #2568. Keyed by the JWT `sub` claim so a different user
 * logging into the same browser starts cold and re-login by the same user
 * keeps the flag.
 *
 * Helpers live in their own module (instead of inline in SessionProvider)
 * so the persistence layer is unit-testable without rendering a React tree.
 */

const WARMUP_FLAG_PREFIX = 'xavior_warmed_';

/**
 * Extracts a stable user identifier from a JWT. Returns the first non-empty
 * value from `sub`, `user_id`, or `userId` claims. Returns null for non-JWT
 * inputs (e.g. legacy hex session tokens), malformed tokens, or tokens
 * missing all three claim aliases.
 *
 * Identity is the canonical `sub` claim for new tokens; the fallbacks
 * (`user_id`, `userId`) appear on older paths in the codebase.
 */
export function getSubFromToken(token: string | null): string | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const payload = parts[1];
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padding = (4 - (normalized.length % 4)) % 4;
    const padded = normalized.padEnd(normalized.length + padding, '=');
    const decoded = typeof atob !== 'undefined'
      ? atob(padded)
      : Buffer.from(padded, 'base64').toString('binary');
    const claims = JSON.parse(decoded) as Record<string, unknown>;
    const sub = claims.sub ?? claims.user_id ?? claims.userId;
    return typeof sub === 'string' && sub.length > 0 ? sub : null;
  } catch {
    return null;
  }
}

export function readWarmupFlag(userId: string | null): boolean {
  if (typeof window === 'undefined' || !userId) return false;
  return localStorage.getItem(`${WARMUP_FLAG_PREFIX}${userId}`) === '1';
}

export function writeWarmupFlag(userId: string | null): void {
  if (typeof window === 'undefined' || !userId) return;
  localStorage.setItem(`${WARMUP_FLAG_PREFIX}${userId}`, '1');
}

export function clearWarmupFlag(userId: string | null): void {
  if (typeof window === 'undefined' || !userId) return;
  localStorage.removeItem(`${WARMUP_FLAG_PREFIX}${userId}`);
}
